/**
 * Consulta ids de transacción (individual o lote) contra el backend.
 * Documentación: GET …/api/orders/lookup/transaction — hasta 40 ids por request; plural `id_transacciones`.
 *
 * Si `REACT_APP_API_URL` ya incluye `/api`, usar ruta `/orders/lookup/transaction` en su lugar.
 */
import axios from 'axios'
import axiosInstance from '../../api/axiosInstance'

/**
 * Ruta relativa al baseURL. Por defecto `/api/orders/lookup/transaction` (doc).
 * Si `REACT_APP_API_URL` ya termina en `/api`, definir `REACT_APP_ORDERS_LOOKUP_PATH=/orders/lookup/transaction`.
 */
export const ORDERS_TRANSACTION_LOOKUP_PATH =
  (typeof process !== 'undefined' && process.env.REACT_APP_ORDERS_LOOKUP_PATH?.trim()) ||
  '/orders/lookup/transaction'

export const LOOKUP_MAX_IDS_PER_REQUEST = 40
export const LOOKUP_MAX_ID_LENGTH = 256

export interface LookupPedidoRow {
  id_pedido?: number
  id_pedido_sistema?: number | string
}

export interface LookupTransactionData {
  id_transaccion?: string
  found?: boolean
  pertenece_a?: string
  pedidos?: LookupPedidoRow[]
  pedidos_pagos?: { found?: boolean; count?: number; matches?: unknown[] }
  pagos_bold?: { found?: boolean; count?: number; matches?: unknown[] }
  /** Respuesta lote (2+ ids) */
  mode?: 'batch'
  count?: number
  results?: LookupTransactionData[]
}

export interface LookupTransactionResponse {
  success?: boolean
  data?: LookupTransactionData
  message?: string
  error?: string
  code?: string
}

function normId(value: string): string {
  return value.trim()
}

/**
 * Normaliza lista de ids: trim, descarta vacíos, dedupe conservando orden, recorta a LOOKUP_MAX_ID_LENGTH.
 */
function normalizeLookupTransactionIds(ids: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of ids) {
    const t = normId(String(raw ?? ''))
    if (!t) continue
    const capped = t.length > LOOKUP_MAX_ID_LENGTH ? t.slice(0, LOOKUP_MAX_ID_LENGTH) : t
    if (seen.has(capped)) continue
    seen.add(capped)
    out.push(capped)
  }
  return out
}

function chunkIds<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

/**
 * Convierte `data` del API en lista `{ id, data }` (un elemento en individual, N en batch).
 */
function flattenLookupResponseData(data: LookupTransactionData | undefined): { id: string; data: LookupTransactionData }[] {
  if (!data || typeof data !== 'object') return []
  if (data.mode === 'batch' && Array.isArray(data.results)) {
    return data.results.map((item) => ({
      id: normId(String(item?.id_transaccion ?? '')),
      data: item
    }))
  }
  const id = normId(String(data.id_transaccion ?? ''))
  return [{ id, data }]
}

/** Reordena resultados del API según el orden de `ids` solicitados (misma clave trim). */
function alignResultsToRequestedIds(
  requestedIds: string[],
  parsed: { id: string; data: LookupTransactionData }[]
): { id: string; data: LookupTransactionData }[] {
  const map = new Map<string, LookupTransactionData>()
  for (const { id, data } of parsed) {
    if (id) map.set(id, data)
  }
  return requestedIds.map((id) => ({
    id,
    data: map.get(id) ?? { id_transaccion: id, found: false }
  }))
}

async function getLookup(payload: Record<string, unknown>): Promise<LookupTransactionData | undefined> {
  const response = await axiosInstance.get<LookupTransactionResponse>(ORDERS_TRANSACTION_LOOKUP_PATH, {
    params: payload
  })
  const body = response.data
  if (body?.success === false && body?.message) {
    throw new Error(body.message)
  }
  return body?.data
}

/**
 * Consulta uno o varios ids en el mismo endpoint (lote si hay 2+ en el chunk).
 * Si hay más de 40 ids, hace varias peticiones en secuencia.
 */
export async function lookupTransactionIds(idsInput: string[]): Promise<{ id: string; data: LookupTransactionData }[]> {
  const ids = normalizeLookupTransactionIds(idsInput)
  if (ids.length === 0) return []

  const allOut: { id: string; data: LookupTransactionData }[] = []

  for (const chunk of chunkIds(ids, LOOKUP_MAX_IDS_PER_REQUEST)) {
    let raw: LookupTransactionData | undefined
    if (chunk.length === 1) {
      raw = await getLookup({ id_transaccion: chunk[0] })
    } else {
      raw = await getLookup({ id_transacciones: chunk.join(',') })
    }
    const flat = flattenLookupResponseData(raw)
    const aligned = alignResultsToRequestedIds(chunk, flat)
    allOut.push(...aligned)
  }

  return allOut
}

export function getLookupErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined
    const msg =
      (typeof data?.message === 'string' && data.message) ||
      (typeof data?.error === 'string' && data.error) ||
      err.message
    if (msg) return msg
    if (err.response?.status === 401) return 'No autorizado para consultar transacciones'
    if (err.response?.status === 403) return 'Sin permiso para consultar transacciones (view_orders)'
    if (err.response?.status === 400 && typeof data?.code === 'string' && data.code === 'INVALID_INPUT') {
      return typeof data?.message === 'string' ? data.message : 'Parámetros de consulta inválidos (ids)'
    }
  }
  if (err instanceof Error) return err.message
  return 'Error al validar el ID de transacción'
}
